import { useCollectionServices } from '../../../contexts/CollectionSourceContext';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Typography } from '@mui/material';
import { FitbitExtractionJob } from '../../../services/fitbitExtractionService';

export default function FitbitExtractionDeleteDialog({ studyId, job, onClose, onDeleted }: {
  studyId: string; job: FitbitExtractionJob; onClose: () => void; onDeleted: (jobId: string) => void;
}) {
  const { deleteFitbitExtraction } = useCollectionServices();
  const [deleteCloudFile, setDeleteCloudFile] = useState(job.deletion?.deleteCloudFile ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(!!job.deletion);
  const alive = useRef(true);
  const pending = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const remove = async () => {
    if (pending.current) return;
    pending.current = true; setBusy(true); setError(''); setSubmitted(true);
    try {
      await deleteFitbitExtraction(studyId, job.jobId, deleteCloudFile);
      if (alive.current) onDeleted(job.jobId);
    } catch (failure) { if (alive.current) setError(failure instanceof Error ? failure.message : 'Could not delete this job. Please retry.'); }
    finally { pending.current = false; if (alive.current) setBusy(false); }
  };
  return <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="delete-extraction-title" PaperProps={{ sx: { borderRadius: '16px' } }}>
    <DialogTitle id="delete-extraction-title">Delete extraction job?</DialogTitle>
    <DialogContent>
      <Stack spacing={1.5}>
        <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{job.name}</Typography>
        <Typography variant="body2">This removes the job from your extraction history. Collected participant data is kept.</Typography>
        {job.kind === 'batch' && <Typography variant="body2">This includes all {job.participantCount} participants in this extraction. The cloud-file choice below applies to all their files.</Typography>}
        {job.state === 'queued' && <Typography variant="body2">The queued extraction will be canceled.</Typography>}
        <FormControlLabel control={<Checkbox checked={deleteCloudFile} disabled={busy || submitted} onChange={event => setDeleteCloudFile(event.target.checked)} />}
          label="Also delete this job’s files from Google Cloud" />
        <Typography variant="caption" color="text.secondary">{deleteCloudFile
          ? 'Cloud retention settings may keep recoverable copies after deletion.'
          : 'The exported file will remain available in your Google Cloud bucket.'}</Typography>
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'center', '& .MuiButton-root': { borderRadius: '9px', minWidth: 120 } }}>
      <Button disabled={busy} onClick={onClose}>Keep job</Button>
      <Button color="error" variant="contained" disableElevation disabled={busy} onClick={() => { void remove(); }}>{busy ? 'Deleting…' : 'Delete job'}</Button>
    </DialogActions>
  </Dialog>;
}
